// A FOTO de quem vende, em qualquer tela do CRM (15/09/2026, pedido do dono:
// "em todos os lugares que pode foto, e clicar para ver no tamanho real").
//
// De onde vem: a consultora/líder/empresária/distribuição sobe a foto pelo
// botão redondo do app.twms. O CRM lê o mapa código do Draco → URL por
// GET /api/app-twms/fotos (lib/app-twms-fotos.cjs, cache de 5 min, fail-open).
// Aqui o mapa é buscado UMA vez por aba do navegador e dividido por todas as
// telas — uma lista de 300 líderes não pode virar 300 pedidos.
//
// Decisões:
//  · Sem foto (ou a URL quebrou), ficam as INICIAIS — nunca um buraco.
//  · Clicar abre a foto no TAMANHO REAL (o app guarda 512×512) num <dialog>
//    nativo (top layer), por cima de tudo. O visor fecha no ✕, no Esc ou no clique no fundo escuro —
//    é um visor, não um formulário: não há nada digitado para perder.
//  · O Esc do visor NÃO chega ao painel lateral de baixo (que também fecha no
//    Esc): o ouvinte é de CAPTURA e para a propagação.
//  · Dentro de algo que já é clicável (linha de lista que abre o detalhe), passe
//    `expandir={false}`: botão dentro de botão é HTML inválido e o clique ficaria
//    ambíguo. O detalhe que a linha abre tem a foto que expande.
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { FiX } from 'react-icons/fi'

type Mapa = Record<string, string>

const VALE_MS = 5 * 60 * 1000
let mapa: Mapa = {}
let buscadoEm = 0
let buscando: Promise<void> | null = null
const ouvintes = new Set<() => void>()

function buscar() {
  if (buscando || Date.now() - buscadoEm < VALE_MS) return
  const token = (() => { try { return localStorage.getItem('auth-token') } catch { return null } })()
  if (!token) return
  buscando = fetch('/api/app-twms/fotos', { headers: { Authorization: `Bearer ${token}` } })
    .then(r => (r.ok ? r.json() : null))
    .then(j => {
      if (j && j.fotos && typeof j.fotos === 'object') { mapa = j.fotos; ouvintes.forEach(f => f()) }
    })
    .catch(() => { /* foto é enfeite: sem ela, ficam as iniciais */ })
    .finally(() => { buscadoEm = Date.now(); buscando = null })
}

function assinar(f: () => void) {
  ouvintes.add(f)
  buscar()
  return () => { ouvintes.delete(f) }
}

/** O mapa código do Draco → URL da foto, compartilhado por todas as telas. */
export function useFotosApp(): Mapa {
  return useSyncExternalStore(assinar, () => mapa, () => mapa)
}

/** A URL da foto de um código (ou null). */
export function useFotoDe(codigo?: string | number | null): string | null {
  const m = useFotosApp()
  return codigo != null && codigo !== '' ? m[String(codigo)] || null : null
}

/**
 * A MINIATURA de uma foto do app (15/09/2026): `/fotos/<sorteado>.jpg` tem um
 * irmão `<sorteado>-p.webp` de 128 px (~2–4 KB contra ~65 KB). Serve para
 * qualquer círculo até 96 px (nítido em tela 2×). URL que não é do app (foto
 * do WhatsApp) volta igual. Se a miniatura faltar, o <img> cai para a inteira.
 */
export function miniDaFoto(url?: string | null): string | null {
  if (!url) return null
  return url.replace(/(\/fotos\/[0-9a-f]{32})\.jpg(?=$|[?#])/, '$1-p.webp')
}

/** A miniatura de um código, para quem monta HTML à mão (pino do Leaflet). */
export function useMiniDe(codigo?: string | number | null): string | null {
  return miniDaFoto(useFotoDe(codigo))
}

const iniciaisDe = (nome?: string | null) =>
  String(nome || '?').trim().split(/\s+/).filter(p => !/^(d[aeo]s?|e)$/i.test(p)).slice(0, 2).map(p => p[0]).join('').toUpperCase() || '?'

export function FotoPessoa({ codigo, foto, reserva, nome, tamanho = 36, expandir = true, className = '', classeIniciais }: {
  /** Código do Draco. A foto sai do mapa do app. */
  codigo?: string | number | null
  /** URL já conhecida (ex.: veio na própria resposta da API) — vence o mapa. */
  foto?: string | null
  /** Usada só se não houver foto do app (ex.: a foto do WhatsApp no Mapa). */
  reserva?: string | null
  nome?: string | null
  tamanho?: number
  expandir?: boolean
  className?: string
  /** Classes do círculo de iniciais, para a tela manter a cor que já tinha. */
  classeIniciais?: string
}) {
  const doMapa = useFotoDe(foto ? null : codigo)
  const url = foto || doMapa || reserva || null
  const [quebrou, setQuebrou] = useState(false)
  const [semMini, setSemMini] = useState(false)
  const [aberta, setAberta] = useState(false)
  useEffect(() => { setQuebrou(false); setSemMini(false) }, [url])
  // Até 96 px, a miniatura; o visor sempre pede a foto inteira.
  const mini = tamanho <= 96 && !semMini ? miniDaFoto(url) : null
  const src = mini && mini !== url ? mini : url

  const st = { width: tamanho, height: tamanho }
  if (!url || quebrou) {
    return (
      <span style={{ ...st, fontSize: Math.round(tamanho * 0.36) }} aria-hidden="true"
        className={`rounded-full shrink-0 inline-flex items-center justify-center font-semibold select-none ${classeIniciais || 'bg-foreground/[0.07] text-muted-foreground'} ${className}`}>
        {iniciaisDe(nome)}
      </span>
    )
  }
  const img = (
    <img src={src || undefined} alt={expandir ? '' : nome ? `Foto de ${nome}` : 'Foto'} style={st} loading="lazy" decoding="async"
      onError={() => (src !== url ? setSemMini(true) : setQuebrou(true))} className={`rounded-full object-cover shrink-0 bg-muted ${expandir ? '' : className}`} />
  )
  if (!expandir) return img
  return (
    <>
      <button type="button" onClick={e => { e.stopPropagation(); setAberta(true) }}
        aria-label={nome ? `Ver a foto de ${nome} no tamanho real` : 'Ver a foto no tamanho real'}
        title="Ver a foto no tamanho real"
        className={`rounded-full shrink-0 p-0 cursor-zoom-in focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${className}`}
        style={st}>
        {img}
      </button>
      {aberta && <VisorFoto url={url} nome={nome} aoFechar={() => setAberta(false)} />}
    </>
  )
}

export function VisorFoto({ url, nome, aoFechar }: { url: string; nome?: string | null; aoFechar: () => void }) {
  // <dialog> NATIVO com showModal() (15/09/2026): ele sobe para a "top layer" do
  // navegador, ACIMA de qualquer z-index. O visor anterior era um <div> fixo com
  // z-[400] no body — e o CRM inteiro mora num contêiner com z-index 10000: o
  // visor abria ATRÁS do painel lateral e o clique "não fazia nada".
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const d = ref.current
    if (d && !d.open) {
      try { d.showModal() } catch { d.setAttribute('open', '') }
    }
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation(); e.preventDefault()
      aoFechar()
    }
    window.addEventListener('keydown', h, true)
    return () => window.removeEventListener('keydown', h, true)
  }, [aoFechar])

  return createPortal(
    <dialog ref={ref} aria-label={nome ? `Foto de ${nome}` : 'Foto'}
      onCancel={e => { e.preventDefault(); aoFechar() }}
      onClick={e => e.stopPropagation()}
      style={{ margin: 0, padding: 0, border: 0, width: '100vw', height: '100dvh', maxWidth: 'none', maxHeight: 'none', background: 'transparent', overflow: 'hidden' }}>
      <div className="w-full h-full bg-black/80 flex flex-col items-center justify-center gap-3 p-4"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        onClick={aoFechar}>
        <button type="button" onClick={e => { e.stopPropagation(); aoFechar() }} aria-label="Fechar a foto" autoFocus
          className="absolute right-3 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white inline-flex items-center justify-center"
          style={{ top: 'max(0.75rem, env(safe-area-inset-top))' }}>
          <FiX className="w-5 h-5" />
        </button>
        {/* Tamanho REAL da foto (512 px no app), encolhendo só se a tela for menor. */}
        <img src={url} alt={nome ? `Foto de ${nome}` : 'Foto'} onClick={e => e.stopPropagation()}
          className="block w-auto h-auto max-w-full rounded-xl shadow-2xl bg-muted cursor-default"
          style={{ maxHeight: 'calc(100dvh - 7rem)' }} />
        {nome && <p className="text-white/90 text-sm font-medium text-center px-4" onClick={e => e.stopPropagation()}>{nome}</p>}
      </div>
    </dialog>,
    document.body,
  )
}
